<template>
  <div class="import-section source-docs-section">
    <div class="import-section-header">
      <div class="import-label">
        <i class="fas fa-file-invoice-dollar"></i>
        Paid invoices (pass-through)
      </div>
    </div>
    <p class="source-purpose">
      Upload invoices you already paid (Cursor, AWS, etc.) for this client’s billing period.
      We extract amounts for expense lines and keep copies to send with your invoice.
    </p>

    <div
      v-if="importReady"
      class="drop-zone"
      :class="{ dragging: isDragging, disabled: uploading }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        class="hidden-input"
        @change="onFileSelect"
      />
      <i class="fas fa-cloud-upload-alt"></i>
      <span v-if="uploading">Uploading and extracting…</span>
      <span v-else>Drop paid vendor PDFs or images here, or click to browse</span>
    </div>
    <p v-if="uploadError" class="import-error">{{ uploadError }}</p>

    <div v-if="resolvedInvoiceId && importableExpenses.length" class="owner-expenses-block">
      <div class="owner-expenses-header">
        <span>From owner expense book (same billing period)</span>
        <button
          class="btn-sm"
          type="button"
          :disabled="!selectedExpenseIds.length || loadingExpenses"
          @click="importSelectedExpenses"
        >
          Add {{ selectedExpenseIds.length }} as expense lines
        </button>
      </div>
      <label
        v-for="exp in importableExpenses"
        :key="exp.id"
        class="expense-row"
      >
        <input v-model="selectedExpenseIds" type="checkbox" :value="exp.id" />
        <span class="exp-vendor">{{ exp.vendor }}</span>
        <span class="exp-desc">{{ exp.description }}</span>
        <span class="exp-amt">${{ exp.amount_usd }}</span>
      </label>
    </div>

    <div v-if="sourceDocuments.length" class="source-doc-list">
      <div v-for="doc in sourceDocuments" :key="doc.id" class="source-doc-card">
        <div class="source-doc-head">
          <strong>{{ doc.vendor || doc.original_filename }}</strong>
          <span v-if="doc.amount_usd" class="source-amt">${{ doc.amount_usd }}</span>
        </div>
        <div class="source-meta">
          <span v-if="doc.source_invoice_number">#{{ doc.source_invoice_number }}</span>
          <span v-if="doc.source_date">{{ formatDate(doc.source_date) }}</span>
        </div>
        <div v-if="extractedLines(doc).length" class="proposed-lines">
          <label
            v-for="(line, idx) in extractedLines(doc)"
            :key="idx"
            class="proposed-line"
          >
            <input
              v-model="docSelectedLines[doc.id]"
              type="checkbox"
              :value="idx"
            />
            {{ line.description }} — ${{ line.amount }}
          </label>
        </div>
        <div class="source-actions">
          <button
            type="button"
            class="btn-sm btn-sm-primary"
            :disabled="addingLines === doc.id"
            @click="addLinesFromDoc(doc)"
          >
            {{ addingLines === doc.id ? 'Adding…' : 'Add as expense lines' }}
          </button>
          <button type="button" class="btn-sm" @click="removeDoc(doc)">Remove</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'InvoiceSourceDocumentsPanel',

  props: {
    invoiceId: {
      type: [Number, String],
      default: null,
    },
    importReady: {
      type: Boolean,
      default: false,
    },
    ensureDraftSaved: {
      type: Function,
      default: null,
    },
  },

  emits: ['lines-added', 'draft-saved'],

  data() {
    return {
      sourceDocuments: [],
      importableExpenses: [],
      selectedExpenseIds: [],
      docSelectedLines: {},
      uploading: false,
      uploadError: '',
      isDragging: false,
      loadingExpenses: false,
      addingLines: null,
    };
  },

  computed: {
    resolvedInvoiceId() {
      return this.invoiceId ? Number(this.invoiceId) : null;
    },
  },

  watch: {
    invoiceId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadSourceDocuments();
          this.loadImportableExpenses();
        } else {
          this.sourceDocuments = [];
          this.importableExpenses = [];
        }
      },
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },

    extractedLines(doc) {
      return doc.proposed_lines || [];
    },

    async loadSourceDocuments(forInvoiceId) {
      const id = forInvoiceId || this.resolvedInvoiceId;
      if (!id) return;
      try {
        const res = await api.get(`/invoices/${id}/source-documents/`);
        this.sourceDocuments = res.data;
        this.sourceDocuments.forEach((doc) => {
          if (!this.docSelectedLines[doc.id]) {
            const lines = this.extractedLines(doc);
            this.docSelectedLines[doc.id] = lines.map((_, i) => i);
          }
        });
      } catch (e) {
        this.uploadError = e.response?.data?.detail || e.message;
      }
    },

    async loadImportableExpenses() {
      await this.loadImportableExpensesFor(this.resolvedInvoiceId);
    },

    async loadImportableExpensesFor(invoiceId) {
      if (!invoiceId) return;
      try {
        const res = await api.get(`/invoices/${invoiceId}/importable-expenses/`);
        this.importableExpenses = res.data;
      } catch {
        this.importableExpenses = [];
      }
    },

    triggerFileInput() {
      if (this.uploading || !this.importReady) return;
      this.$refs.fileInput?.click();
    },

    onDrop(event) {
      this.isDragging = false;
      if (!this.importReady) return;
      const files = Array.from(event.dataTransfer?.files || []);
      this.uploadFiles(files);
    },

    onFileSelect(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      this.uploadFiles(files);
    },

    async uploadFiles(files) {
      if (!files.length) return;
      this.uploadError = '';
      let invoiceId = this.resolvedInvoiceId;
      if (!invoiceId && this.ensureDraftSaved) {
        try {
          invoiceId = await this.ensureDraftSaved();
        } catch (e) {
          this.uploadError = e.message || 'Could not save draft invoice.';
          return;
        }
      }
      if (!invoiceId) {
        this.uploadError = 'Select a client and save a draft before uploading paid invoices.';
        return;
      }
      this.uploading = true;
      try {
        for (const file of files) {
          const form = new FormData();
          form.append('file', file);
          await api.post(`/invoices/${invoiceId}/source-documents/`, form);
        }
        if (invoiceId !== this.resolvedInvoiceId) {
          this.$emit('draft-saved', invoiceId);
        }
        await this.loadSourceDocuments(invoiceId);
        await this.loadImportableExpensesFor(invoiceId);
      } catch (e) {
        this.uploadError = e.response?.data?.detail || e.message;
      } finally {
        this.uploading = false;
      }
    },

    async addLinesFromDoc(doc) {
      if (!this.resolvedInvoiceId) return;
      this.addingLines = doc.id;
      try {
        const selected = this.docSelectedLines[doc.id];
        const payload = selected?.length
          ? { selected_indices: selected }
          : {};
        const res = await api.post(
          `/invoices/${this.resolvedInvoiceId}/source-documents/${doc.id}/add-lines/`,
          payload,
        );
        this.$emit('lines-added', res.data);
      } catch (e) {
        this.uploadError = e.response?.data?.detail || e.message;
      } finally {
        this.addingLines = null;
      }
    },

    async removeDoc(doc) {
      if (!this.resolvedInvoiceId) return;
      try {
        await api.delete(
          `/invoices/${this.resolvedInvoiceId}/source-documents/${doc.id}/`,
        );
        await this.loadSourceDocuments();
      } catch (e) {
        this.uploadError = e.response?.data?.detail || e.message;
      }
    },

    async importSelectedExpenses() {
      if (!this.resolvedInvoiceId || !this.selectedExpenseIds.length) return;
      this.loadingExpenses = true;
      try {
        const res = await api.post(
          `/invoices/${this.resolvedInvoiceId}/import-expense-lines/`,
          { expense_ids: this.selectedExpenseIds },
        );
        this.$emit('lines-added', res.data);
        this.selectedExpenseIds = [];
      } catch (e) {
        this.uploadError = e.response?.data?.detail || e.message;
      } finally {
        this.loadingExpenses = false;
      }
    },
  },
};
</script>

<style scoped>
.source-purpose {
  font-size: 0.85rem;
  color: var(--text-secondary, #6c7a9c);
  margin: 0 0 12px;
  line-height: 1.45;
}

.drop-zone {
  border: 2px dashed var(--border, #c5cde0);
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  color: var(--text-secondary, #6c7a9c);
  transition: border-color 0.15s, background 0.15s;
}

.drop-zone.dragging,
.drop-zone:hover:not(.disabled) {
  border-color: var(--accent, #2c3e8c);
  background: rgba(44, 62, 140, 0.04);
}

.drop-zone.disabled {
  opacity: 0.7;
  cursor: wait;
}

.hidden-input {
  display: none;
}

.source-doc-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.source-doc-card {
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-muted, #f8f9fc);
}

.source-doc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.source-meta {
  font-size: 0.8rem;
  color: var(--text-secondary, #6c7a9c);
  margin-top: 4px;
  display: flex;
  gap: 12px;
}

.proposed-lines {
  margin-top: 8px;
  font-size: 0.85rem;
}

.proposed-line {
  display: block;
  margin: 4px 0;
}

.source-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.owner-expenses-block {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border, #e0e4f0);
}

.owner-expenses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.85rem;
}

.expense-row {
  display: grid;
  grid-template-columns: auto 80px 1fr auto;
  gap: 8px;
  align-items: center;
  font-size: 0.85rem;
  padding: 4px 0;
}

.exp-amt {
  font-weight: 600;
  text-align: right;
}
</style>
